import { PLACE_ID_PATTERN } from '@bandido/shared/types';
import type { Place } from '@bandido/shared/types';

import { countries } from '@frontend/features/common/constants/countries';
import { CatalogEntry } from './queries';

/**
 * Nombres de destino en el idioma del usuario.
 *
 * Los catálogos de los proveedores vienen SOLO en inglés. Para países alcanza
 * `Intl.DisplayNames` (nativo del browser, cero dependencias): traduce el ISO
 * 3166-1 alpha-2 a cualquier idioma. Las regiones son slugs propios del
 * proveedor (`europe`, `eu-plus-uk`), así que llevan un mapeo local corto.
 *
 * NOTA de alcance: esto es una traducción de conveniencia, NO el árbol de
 * destinos canónico (regiones/países/ciudades/barrios + IATA, multilingüe y
 * buscable en cualquier idioma). Eso es una feature propia — ver
 * docs/RUTERO.md, sección "Fuera de alcance", y la Fase B de destinos
 * canónicos del hub.
 */

/** Regiones conocidas de los proveedores de eSIM (slug → nombre por idioma). */
const REGION_NAMES: Record<string, Record<string, string>> = {
  africa: { es: 'África', en: 'Africa' },
  asia: { es: 'Asia', en: 'Asia' },
  'asia-pacific': { es: 'Asia-Pacífico', en: 'Asia-Pacific' },
  caribbean: { es: 'Caribe', en: 'Caribbean' },
  europe: { es: 'Europa', en: 'Europe' },
  'eu-plus-uk': {
    es: 'Unión Europea y Reino Unido',
    en: 'European Union and United Kingdom',
  },
  'latin-america': { es: 'Latinoamérica', en: 'Latin America' },
  'middle-east': { es: 'Medio Oriente', en: 'Middle East' },
  'north-america': { es: 'Norteamérica', en: 'North America' },
  oceania: { es: 'Oceanía', en: 'Oceania' },
  'south-america': { es: 'Sudamérica', en: 'South America' },
  world: { es: 'Mundial', en: 'World' },
  global: { es: 'Global', en: 'Global' },
};

const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

function regionDisplayNames(language: string): Intl.DisplayNames | null {
  if (!displayNamesCache.has(language)) {
    try {
      displayNamesCache.set(
        language,
        new Intl.DisplayNames([language], { type: 'region' }),
      );
    } catch {
      displayNamesCache.set(language, null);
    }
  }
  return displayNamesCache.get(language) ?? null;
}

export interface CountryOption {
  code: string;
  name: string;
}

/**
 * Nombre del país en el idioma del usuario a partir del ISO-2. Devuelve
 * `undefined` si el código no es válido o `Intl` no lo conoce (así el caller
 * puede caer a lo que publique el proveedor).
 */
export function countryName(
  code: string | undefined,
  language: string,
): string | undefined {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return undefined;
  const upper = code.toUpperCase();
  const translated = regionDisplayNames((language || 'es').split('-')[0])?.of(
    upper,
  );
  return translated && translated !== upper ? translated : undefined;
}

/**
 * Países ISO-2 con el nombre en el idioma del usuario, ordenados alfabéticamente.
 * Los usan los proveedores que tarifan por residencia (autos) o exigen
 * nacionalidad del pasajero (ferry).
 */
export function countryOptions(language: string): CountryOption[] {
  const displayNames = regionDisplayNames((language || 'es').split('-')[0]);
  return countries
    .map((country) => ({
      code: country.code,
      name: displayNames?.of(country.code) ?? country.name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function isCountryEntry(entry?: CatalogEntry | null): boolean {
  return Boolean(entry?.countryCode) || entry?.type === 'country';
}

/** Nombre traducido, con fallback al que publica el proveedor. */
export function destinationLabel(
  entry: CatalogEntry,
  language: string,
): string {
  const [lang] = (language || 'es').split('-');

  if (isCountryEntry(entry)) {
    const code = (entry.countryCode ?? entry.id).toUpperCase();
    if (/^[A-Z]{2}$/.test(code)) {
      const translated = regionDisplayNames(lang)?.of(code);
      // Intl devuelve el código cuando no conoce la región.
      if (translated && translated !== code) return translated;
    }
    return entry.name;
  }

  const slug = entry.id.toLowerCase();
  return REGION_NAMES[slug]?.[lang] ?? entry.name;
}

/**
 * Matchea contra el nombre traducido Y el original (así "turq", "turkey" y
 * "TR" encuentran Turquía), ignorando acentos.
 */
export function matchesDestination(
  entry: CatalogEntry,
  query: string,
  language: string,
): boolean {
  const needle = normalize(query);
  if (!needle) return true;
  return [destinationLabel(entry, language), entry.name, entry.id].some(
    (candidate) => normalize(candidate).includes(needle),
  );
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();
}

// ── País por places, región del catálogo del hub (#2174 — híbrido) ────────

/**
 * Adapta un país del maestro de lugares al shape de `CatalogEntry` que ya
 * usa el picker de eSIMs: países por places, regiones del catálogo del
 * hub, en el MISMO control y la MISMA lista (`options`) — no hay picker
 * que swapear, solo una fuente distinta para la mitad "país" de la lista.
 *
 * `type: 'country'` satisface `isCountryEntry` sin necesitar un
 * `countryCode` ISO real (el `id` acá es un `plc_*`, no un ISO-2) — y el
 * `name` YA viene traducido por ubikeishon (`lang`), así que
 * `destinationLabel` no necesita re-traducirlo: su intento de
 * `Intl.DisplayNames` simplemente no dispara, porque un `plc_*` no matchea
 * `/^[A-Z]{2}$/`.
 *
 * `meta.source: 'places'` es la marca que decide, al buscar, si el `id`
 * elegido es un `plc_*` (mandar `destination`) o un país del catálogo
 * viejo (mandar `country`) — se lee de LA SELECCIÓN (`isPlacesSourcedDestination`),
 * nunca del valor VIVO del setting: si el setting cambia después de elegir,
 * la búsqueda tiene que seguir usando el formato con el que se eligió.
 */
/**
 * La lista del picker de eSIMs, en su forma híbrida: países primero, regiones
 * después, acotada a 60.
 *
 * Los países vienen del maestro (ya rankeados y filtrados por ubikeishon) y
 * del catálogo del hub se usan SOLO las regiones. Si Places no está listo,
 * los países quedan vacíos: nunca se recuperan códigos nativos de proveedor.
 *
 * Pura y testeable a propósito: la invariante que importa —**si el maestro
 * falla, los países desaparecen pero las REGIONES siguen elegibles**— no se
 * puede verificar desde el `.tsx` (este repo no testea componentes). Que la
 * lista quede sin países es correcto; lo que no puede pasar es que además se
 * lea como "no hay destinos", y de eso se ocupa el aviso del formulario.
 */
export function esimsDestinationOptions({
  placesReady,
  placeCountries,
  catalog,
  query,
  language,
}: {
  placesReady: boolean;
  placeCountries: CatalogEntry[];
  catalog: CatalogEntry[] | undefined;
  query: string;
  language: string;
}): CatalogEntry[] {
  const entries = catalog ?? [];
  const regions = entries
    .filter((entry) => !isCountryEntry(entry))
    .filter((entry) => matchesDestination(entry, query, language));
  return [...(placesReady ? placeCountries : []), ...regions].slice(0, 60);
}

export function placeToDestinationEntry(place: Pick<Place, 'id' | 'name'>): CatalogEntry {
  return {
    id: place.id,
    name: place.name,
    type: 'country',
    meta: { source: 'places' },
  };
}

/**
 * ¿Este destino elegido vino del maestro de lugares (`plc_*`)? Exige AMBAS
 * señales — `meta.source` Y que `id` tenga forma de `plc_*` — no solo la
 * primera: el catálogo de eSIMs (`esims/destinations`) es contenido de un
 * proveedor externo sin allowlist de campos (a diferencia de otros
 * catálogos del hub), así que un `meta.source: 'places'` que viniera DE ESE
 * catálogo (coincidencia, no bandido) no alcanza por sí solo para que se
 * mande como `destination` — tiene que además parecer, de verdad, un
 * `plc_*`.
 */
export function isPlacesSourcedDestination(entry?: CatalogEntry | null): boolean {
  return Boolean(entry) && entry?.meta?.source === 'places' && PLACE_ID_PATTERN.test(entry.id);
}
